import {
  GEOFENCE_KINDS,
  circleRing,
  normalizeGeofenceName,
  openRing,
  roleAtLeast,
  type Geofence,
  type GeofenceGeometry,
  type GeofenceIssue,
  type GeofenceKind,
  type GeofenceShapeType,
  type LonLat,
  type Site,
  type TenantRole,
  type VehicleType,
} from "@fts/core";
import type { GeofenceInput } from "../data/provider";
import type { MessageKey, TFn } from "../i18n";

/**
 * ADR 0015: zones are written by safety officers, fleet managers and company admins on their sites
 * (every visible site is accessible); everyone else who sees the site reads them.
 */
export const canEditGeofences = (role: TenantRole | null): boolean => !!role && roleAtLeast(role, "safety");

export const isGeofenceKind = (v: string): v is GeofenceKind => (GEOFENCE_KINDS as readonly string[]).includes(v);

/** CSS custom properties (tokens.css) that colour each kind on maps and legends. */
export const GEOFENCE_COLOR_TOKEN: Record<GeofenceKind, string> = {
  forbidden: "--zone-forbidden",
  work_area: "--zone-work-area",
  dwell: "--zone-dwell",
};

/** Dwell limit in whole minutes for the form (60–86 400 s = 1–1440 min). */
export const DWELL_MIN_MINUTES = 1;
export const DWELL_MAX_MINUTES = 1440;

/** The editable state of the zone dialog. */
export type GeofenceDraft = {
  name: string;
  kind: GeofenceKind;
  shape: GeofenceShapeType;
  vertices: LonLat[];
  center: LonLat | null;
  /** Text of the radius field (metres). */
  radius: string;
  vehicleTypes: VehicleType[];
  /** Text of the dwell field (minutes). */
  dwellMinutes: string;
  active: boolean;
};

export const emptyDraft = (): GeofenceDraft => ({
  name: "",
  kind: "forbidden",
  shape: "polygon",
  vertices: [],
  center: null,
  radius: "20",
  vehicleTypes: [],
  dwellMinutes: "10",
  active: true,
});

export function draftFromGeofence(g: Geofence): GeofenceDraft {
  return {
    name: g.name,
    kind: g.kind,
    shape: g.shape,
    vertices: g.shape === "polygon" ? openRing(g.coords ?? []) : [],
    center: g.shape === "circle" && g.centerLat !== null && g.centerLon !== null ? [g.centerLon, g.centerLat] : null,
    radius: g.radiusM !== null ? String(g.radiusM) : "20",
    vehicleTypes: [...g.vehicleTypes],
    dwellMinutes: g.dwellLimitS !== null ? String(Math.round(g.dwellLimitS / 60)) : "10",
    active: g.active,
  };
}

const parseNum = (v: string): number | null => {
  const s = v.trim().replace(",", ".");
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

/** Geometry of a draft (incomplete parts stay null so validation reports them). */
export function draftGeometry(d: Pick<GeofenceDraft, "shape" | "vertices" | "center" | "radius">): GeofenceGeometry {
  if (d.shape === "circle") {
    return { shape: "circle", coords: null, centerLat: d.center?.[1] ?? null, centerLon: d.center?.[0] ?? null, radiusM: parseNum(d.radius) };
  }
  return { shape: "polygon", coords: d.vertices, centerLat: null, centerLon: null, radiusM: null };
}

/** Dwell minutes text -> seconds (null when not a whole number). */
export function dwellSeconds(minutes: string): number | null {
  const n = parseNum(minutes);
  return n !== null && Number.isInteger(n) ? n * 60 : null;
}

export function draftToInput(d: GeofenceDraft, ids: { id?: string; tenantId: string; siteId: string }): GeofenceInput {
  const g = draftGeometry(d);
  return {
    ...(ids.id ? { id: ids.id } : {}),
    tenantId: ids.tenantId,
    siteId: ids.siteId,
    name: normalizeGeofenceName(d.name),
    kind: d.kind,
    ...g,
    vehicleTypes: [...d.vehicleTypes],
    dwellLimitS: d.kind === "dwell" ? dwellSeconds(d.dwellMinutes) : null,
    active: d.active,
  };
}

export const GEOFENCE_ISSUE_KEY: Record<GeofenceIssue, MessageKey> = {
  name_required: "geofence.issue.name_required",
  name_too_long: "geofence.issue.name_too_long",
  invalid_kind: "geofence.issue.invalid_kind",
  invalid_shape: "geofence.issue.invalid_shape",
  invalid_coordinate: "geofence.issue.invalid_coordinate",
  too_few_vertices: "geofence.issue.too_few_vertices",
  too_many_vertices: "geofence.issue.too_many_vertices",
  self_intersecting: "geofence.issue.self_intersecting",
  too_narrow: "geofence.issue.too_narrow",
  radius_out_of_range: "geofence.issue.radius_out_of_range",
  dwell_out_of_range: "geofence.issue.dwell_out_of_range",
  dwell_only_for_dwell: "geofence.issue.dwell_only_for_dwell",
  invalid_vehicle_type: "geofence.issue.invalid_vehicle_type",
};

/** "Çokgen · 5 köşe" / "Daire · 15 m". */
export function shapeSummary(t: TFn, g: GeofenceGeometry, locale: string): string {
  if (g.shape === "circle") return t("geofence.shapeCircleSummary", { radius: (g.radiusM ?? 0).toLocaleString(locale, { maximumFractionDigits: 1 }) });
  return t("geofence.shapePolygonSummary", { count: openRing(g.coords ?? []).length });
}

export function vehicleTypesSummary(t: TFn, types: readonly VehicleType[]): string {
  return types.length === 0 ? t("geofence.allVehicleTypes") : types.map((x) => t(`vehicleType.${x}` as MessageKey)).join(", ");
}

/** "10 dk" / "1 sa 30 dk". */
export function formatDwell(t: TFn, seconds: number | null): string {
  if (seconds === null) return t("common.empty");
  const m = Math.round(seconds / 60);
  const h = Math.floor(m / 60);
  const rest = m % 60;
  if (h === 0) return t("geofence.minutes", { m });
  return rest === 0 ? t("geofence.hours", { h }) : t("geofence.hoursMinutes", { h, m: rest });
}

/** One-line description of what triggers the zone's alarm. */
export function kindRule(t: TFn, g: Pick<Geofence, "kind" | "dwellLimitS">): string {
  if (g.kind === "dwell") return t("geofence.rule.dwell", { duration: formatDwell(t, g.dwellLimitS) });
  return t(`geofence.rule.${g.kind}` as MessageKey);
}

/** Display ring [[lon, lat], …] (closed) of a zone; null when the geometry is incomplete. */
export function displayRing(g: GeofenceGeometry): LonLat[] | null {
  if (g.shape === "circle") {
    if (g.centerLat === null || g.centerLon === null || g.radiusM === null || g.radiusM <= 0) return null;
    const ring = circleRing(g.centerLat, g.centerLon, g.radiusM, 64);
    return [...ring, ring[0]!];
  }
  const ring = openRing(g.coords ?? []);
  if (ring.length < 3) return null;
  return [...ring, ring[0]!];
}

type Feature = {
  type: "Feature";
  id?: string;
  properties: { id: string; kind: GeofenceKind; name: string; active: boolean };
  geometry: { type: "Polygon"; coordinates: LonLat[][] };
};
export type GeofenceFeatureCollection = { type: "FeatureCollection"; features: Feature[] };

/** GeoJSON of zones for the map layers (zones with incomplete geometry are left out). */
export function geofenceFeatures(zones: ReadonlyArray<Pick<Geofence, "id" | "kind" | "name" | "active"> & GeofenceGeometry>): GeofenceFeatureCollection {
  const features: Feature[] = [];
  for (const z of zones) {
    const ring = displayRing(z);
    if (!ring) continue;
    features.push({ type: "Feature", properties: { id: z.id, kind: z.kind, name: z.name, active: z.active }, geometry: { type: "Polygon", coordinates: [ring] } });
  }
  return { type: "FeatureCollection", features };
}

/** [[west, south], [east, north]] of the zones, or null. */
export function geofenceBounds(zones: ReadonlyArray<GeofenceGeometry>): [LonLat, LonLat] | null {
  let w = Infinity;
  let s = Infinity;
  let e = -Infinity;
  let n = -Infinity;
  for (const z of zones) {
    for (const [lon, lat] of displayRing(z) ?? []) {
      w = Math.min(w, lon);
      e = Math.max(e, lon);
      s = Math.min(s, lat);
      n = Math.max(n, lat);
    }
  }
  return Number.isFinite(w) ? [[w, s], [e, n]] : null;
}

/** Map starting point for a site: its own point, else the nearest ancestor's; null when none has one. */
export function siteAnchor(sites: Site[], siteId: string): LonLat | null {
  let cur = sites.find((s) => s.id === siteId);
  const seen = new Set<string>();
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    if (cur.lat !== null && cur.lon !== null) return [cur.lon, cur.lat];
    const parentId: string | null = cur.parentId;
    cur = parentId ? sites.find((s) => s.id === parentId) : undefined;
  }
  return null;
}

/** Move a [lon, lat] by metres (keyboard nudging of vertices). */
export function nudge(p: LonLat, eastM: number, northM: number): LonLat {
  const m = 111_194.93;
  return [p[0] + eastM / (m * Math.cos((p[1] * Math.PI) / 180)), p[1] + northM / m];
}
